<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="720px"
    :fullscreen="$vuetify.display.smAndDown"
  >
    <v-card elevation="0">
      <v-container>
        <v-row>
          <!-- CAMERA SCANNER -->
          <v-col v-if="!physicalScanner" cols="12">
            <div class="scanner-wrapper">
              <qrcode-stream
                v-if="dialog"
                :key="cameraFacing"
                :camera="cameraFacing"
                :paused="paused"
                class="insert-parts-qr-stream"
                @decode="decode"
                @init="onInit"
              >
                <template #error="err">
                  <v-alert type="error" variant="tonal">
                    {{ err?.message || $t("Cannot access camera.") }}
                  </v-alert>
                </template>
              </qrcode-stream>
              <div
                v-if="cameraLoading"
                class="scanner-loading-overlay"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="48"
                />
              </div>
            </div>
          </v-col>

          <!-- PHYSICAL SCANNER -->
          <v-col v-else cols="12">
            <v-row class="my-2" justify="center">
              <div
                v-if="!physicalScannerError"
                class="ma-3 text-blue text-subtitle-1"
              >
                {{ $t("shipments.scanner.scan_alert_phys") }}
              </div>
              <div
                v-else
                class="ma-3 text-red text-subtitle-1"
              >
                {{ $t("shipments.scanner.error_alert") }}
              </div>
            </v-row>
          </v-col>

          <!-- LOADER -->
          <v-col cols="12" class="text-center">
            <v-progress-circular v-if="loading" indeterminate color="primary" />
          </v-col>

          <!-- ALERT -->
          <v-col cols="12">
            <v-alert
              v-if="alert.value"
              :type="alert.type"
              variant="tonal"
              border="bottom"
            >
              {{ alert.message }}
            </v-alert>
          </v-col>

          <!-- SELECT SHIPMENT -->
          <v-col v-if="found" cols="12" class="text-center">
            <v-btn
              variant="outlined"
              color="primary"
              @click="selectShipment"
            >
              {{ $t("shipments.scanner.select_shipment") }}
            </v-btn>
          </v-col>

          <!-- BOTTOM ACTION BUTTONS -->
          <v-col cols="12">
            <v-row class="pa-2 align-center">
              <v-btn icon variant="text" @click="reScan">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>

              <v-spacer />

              <v-btn icon variant="text" @click="switchScanner">
                <v-icon>mdi-qrcode</v-icon>
              </v-btn>

              <v-btn
                v-if="!physicalScanner"
                icon
                variant="text"
                @click="switchCamera"
              >
                <v-icon>mdi-camera-switch</v-icon>
              </v-btn>

              <v-btn variant="outlined" color="error" @click="close">
                {{ $t("shipments.scanner.close") }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import getRequest from "../../api/getRequest";
import { QrcodeStream } from "vue3-qrcode-reader";
export default {
  components: {
    QrcodeStream,
  },
  data: () => ({
    dialog: false,
    /** Prefer rear on mobile; 'auto' falls back to front/webcam on desktop. */
    cameraFacing: "auto",
    paused: false,
    cameraLoading: false,
    loading: false,
    alert: {
      value: false,
      type: "success",
      message: "",
    },
    found: null,
    shipmentId: null,
    physicalScanner: false,
    physicalScannerError: false,
    buffer: "",
  }),
  methods: {
    async onInit(promise) {
      this.cameraLoading = true;
      try {
        await promise;
      } catch (err) {
        if (err.name === "NotAllowedError") {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t(
              "Camera access denied. Using physical scanner mode.",
            ),
          };
          this.switchScanner();
        } else if (
          (err.name === "OverconstrainedError" ||
            err.name === "NotFoundError") &&
          this.cameraFacing !== "auto"
        ) {
          this.cameraFacing = "auto";
        } else {
          this.alert = {
            value: true,
            type: "error",
            message: this.$t("Cannot access camera."),
          };
        }
      } finally {
        this.cameraLoading = false;
      }
    },
    listenToEvent(e) {
      if (e.key !== "Enter") {
        this.buffer += e.key;
      } else if (this.buffer.length > 24) {
        this.physicalScannerError = true;
        this.buffer = "";
        this.alert = {
          value: true,
          type: "error",
          message: this.$t("shipments.scanner.error_alert"),
        };
      } else {
        this.decode(this.buffer);
        this.buffer = "";
      }
    },
    switchCamera() {
      this.physicalScanner = false;
      this.alert.message = this.$t("shipments.scanner.scan_alert_cam");
      document.removeEventListener("keydown", this.listenToEvent);
      this.cameraLoading = true;
      this.cameraFacing = this.cameraFacing === "front" ? "rear" : "front";
    },
    switchScanner() {
      this.physicalScanner = true;
      this.cameraLoading = false;
      this.alert.message = this.$t("shipments.scanner.scan_alert_phys");
      document.addEventListener("keydown", this.listenToEvent);
    },
    decode(value) {
      if (value && value.length > 0) {
        this.loading = true;
        this.found = null;
        this.paused = true;
        getRequest("/shipment", `id=${value}`)
          .then((response) => {
            this.loading = false;
            if (response.shipment && response.shipment.length > 0) {
              this.found = true;
              this.shipmentId = response.shipment[0]._id;
              this.alert = {
                value: true,
                type: "success",
                message: `${this.$t("shipments.scanner.found_alert")} ${this.shipmentId}`,
              };
            } else {
              this.found = false;
              this.shipmentId = null;
              this.alert = {
                value: true,
                type: "warning",
                message: `${this.$t("shipments.scanner.not_found_alert")} ${value}`,
              };
            }
          })
          .catch(() => {
            this.loading = false;
            this.found = false;
            this.shipmentId = null;
            this.alert = {
              value: true,
              type: "error",
              message: this.$t("shipments.scanner.error_alert"),
            };
          });
      }
    },
    selectShipment() {
      this.$emit("select-shipment", this.shipmentId);
      this.close();
    },
    reScan() {
      this.found = null;
      this.shipmentId = null;
      this.physicalScannerError = false;
      this.loading = false;
      this.paused = false;
      this.alert = {
        value: true,
        type: "info",
        message: this.physicalScanner
          ? this.$t("shipments.scanner.scan_alert_phys")
          : this.$t("shipments.scanner.scan_alert_cam"),
      };
    },
    open() {
      this.cameraFacing = "auto";
      this.cameraLoading = true;
      this.dialog = true;
      this.alert = {
        value: true,
        type: "info",
        message: this.$t("shipments.scanner.scan_alert_cam"),
      };
    },
    close() {
      this.dialog = false;
      this.paused = false;
      this.loading = false;
      this.found = null;
      this.shipmentId = null;
      this.physicalScanner = false;
      this.physicalScannerError = false;
      this.cameraFacing = "auto";
      this.cameraLoading = false;
      this.alert = {
        value: false,
        type: "success",
        message: "",
      };
      document.removeEventListener("keydown", this.listenToEvent);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.scanner-wrapper {
  position: relative;
  width: 100%;
  min-height: 200px;
}

.scanner-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
}

.insert-parts-qr-stream {
  display: block;
  width: 100%;
  max-height: 55vh;
}

.insert-parts-qr-stream :deep(video) {
  width: 100%;
  max-height: 55vh;
  object-fit: cover;
}

@media (max-width: 959.98px) {
  .insert-parts-qr-stream,
  .insert-parts-qr-stream :deep(video) {
    max-height: calc(100dvh - 280px);
  }
}
</style>
